/**
 * Message composer: multiline input, Enter to send / Shift+Enter newline,
 * Send/Stop toggle while streaming, and a settings entry point (the surface
 * owns the actual settings dialog). Surface-agnostic.
 */

import React, { useState } from 'react';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import TuneIcon from '@mui/icons-material/Tune';

interface ComposerProps {
  disabled: boolean;
  isStreaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  onOpenSettings: () => void;
  placeholder?: string;
}

const Composer: React.FC<ComposerProps> = ({
  disabled,
  isStreaming,
  onSend,
  onStop,
  onOpenSettings,
  placeholder = 'Ask the AI…',
}) => {
  const [value, setValue] = useState('');

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setValue('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.5,
        p: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Tooltip title="AI settings">
        <span>
          <IconButton size="small" onClick={onOpenSettings} aria-label="AI settings">
            <TuneIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={placeholder}
        multiline
        maxRows={6}
        fullWidth
        size="small"
        disabled={disabled && !isStreaming}
      />
      {isStreaming ? (
        <Tooltip title="Stop">
          <IconButton color="error" onClick={onStop} aria-label="Stop generating">
            <StopIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Send">
          <span>
            <IconButton
              color="primary"
              onClick={submit}
              disabled={disabled || !value.trim()}
              aria-label="Send message"
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default Composer;
